import React, { useRef, useEffect } from "react";
import { Temporal } from "temporal-polyfill-lite";
// Components
import { ChatComposeSection } from "../chatComposeSection/ChatComposeSection";
import { Chat } from "@fluentui-contrib/react-chat";
import { VirtualListWrapper, VirtualListOrientation } from "../virtualization/VirtualListWrapper";
// Resources
import chatImageLight from "@ms/uno-resources/lib/local/images/chat-light.png";
import chatImageDark from "@ms/uno-resources/lib/local/images/chat-dark.png";
// Styles
import { customVirtualListWrapperStyles, useClasses } from "./ChatThreadView.styles";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { Spinner, SpinnerSize, useTheme } from "@fluentui/react";
import { isLightTheme } from "../../../utilities/ThemeUtilities";
import { ChatMessageItem } from "./ChatMessageItem";
const scrollThreshold = 10;
/** Number of recent messages to consider for mention suggestions */ const RecentMessagesLimit = 20;
/** How long to display deleted messages after its deletion time */ const DeletedMessageDisplayDurationHours = 24;
export const ChatThreadView = ({ chatMessages, isLoading, hasMore, currentUserId, prioritizedUserIdsForMentionSuggestions, focusOnInit, callbacks })=>{
    const { onSendMessage, onLoadMore, getMentionableUsers, getUserById, getUserPhoto, onRenderPersona } = callbacks;
    const theme = useTheme();
    const classNames = useClasses();
    const wrapperRef = useRef(null);
    const previousContentHeightRef = useRef(0);
    // Track the top and bottom message id to manage scroll position
    const lastTopMessageIdRef = useRef(undefined);
    const lastBottomMessageIdRef = useRef(undefined);
    const now = Temporal.Now.instant();
    const renderedMessages = chatMessages.filter((message)=>{
        // Render non-deleted messages
        if (!message.isDeleted) {
            return true;
        }
        if (!message.deletedTime) {
            // Message is deleted but no deleted time, do not render
            return false;
        }
        // Deleted messages should be rendered if deleted recently
        const hoursSinceDeleted = now.since(message.deletedTime).total("hours");
        const isDeletedRecently = hoursSinceDeleted < DeletedMessageDisplayDurationHours;
        return isDeletedRecently;
    });
    const onLastElementIsVisibleHandler = ()=>{
        if (hasMore) {
            onLoadMore();
        }
    };
    const onSendMessageHandler = (parameters)=>{
        onSendMessage(parameters);
        // Scroll to bottom after sending a message
        if (wrapperRef.current) {
            wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;
        }
    };
    // Effect to handle scroll position adjustment when loading more messages
    useEffect(()=>{
        if (!wrapperRef.current || renderedMessages.length === 0) {
            return;
        }
        const currentContentHeight = wrapperRef.current.scrollHeight;
        const topMsg = renderedMessages[0];
        const bottomMsg = renderedMessages[renderedMessages.length - 1];
        if (previousContentHeightRef.current === 0) {
            // Initialize scroll to bottom
            wrapperRef.current.scrollTop = currentContentHeight;
        } else {
            if (lastTopMessageIdRef.current !== topMsg?.id) {
                // If the top message has changed, adjust scroll to maintain position
                const heightDifference = currentContentHeight - previousContentHeightRef.current;
                wrapperRef.current.scrollTop += heightDifference;
            } else if (lastBottomMessageIdRef.current !== bottomMsg?.id) {
                // If bottom message changed, and we were already at the bottom, then keep scroll at bottom
                const wasAtBottom = wrapperRef.current.scrollTop + wrapperRef.current.clientHeight + scrollThreshold >= previousContentHeightRef.current;
                if (wasAtBottom) {
                    wrapperRef.current.scrollTop = currentContentHeight;
                }
            }
        }
        // Update the tracked values
        previousContentHeightRef.current = currentContentHeight;
        lastTopMessageIdRef.current = topMsg?.id;
        lastBottomMessageIdRef.current = bottomMsg?.id;
    });
    const renderLoadingView = ()=>{
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.loadingContainer
        }, /*#__PURE__*/ React.createElement(Spinner, {
            size: SpinnerSize.large,
            label: Strings.Common_Loading
        }));
    };
    const renderEmptyView = ()=>{
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.emptyContainer
        }, /*#__PURE__*/ React.createElement("div", {
            className: classNames.emptyViewImage
        }, /*#__PURE__*/ React.createElement("img", {
            src: isLightTheme(theme) ? chatImageLight : chatImageDark,
            alt: "",
            role: "presentation"
        })), /*#__PURE__*/ React.createElement("div", {
            className: classNames.emptyViewText,
            "aria-live": "polite"
        }, Strings.TaskChat_EmptyViewText), /*#__PURE__*/ React.createElement("div", {
            className: classNames.emptyViewSubText,
            "aria-live": "polite"
        }, Strings.TaskChat_EmptyViewSubText));
    };
    const renderMessagesView = ()=>{
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.chatMessagesContainer
        }, /*#__PURE__*/ React.createElement(Chat, {
            className: classNames.chatContainer,
            ref: wrapperRef
        }, /*#__PURE__*/ React.createElement(VirtualListWrapper, {
            id: "chat-thread-virtual-list",
            hasMoreItemsToRender: hasMore,
            reversed: true,
            orientation: VirtualListOrientation.Vertical,
            onLastElementIsVisible: onLastElementIsVisibleHandler,
            styles: customVirtualListWrapperStyles
        }, renderedMessages.map((chatMessage, index)=>/*#__PURE__*/ React.createElement(ChatMessageItem, {
                key: chatMessage.id,
                message: chatMessage,
                getUserById: getUserById,
                currentUserId: currentUserId,
                onDeleteMessage: callbacks.onDeleteMessage,
                getUserPhoto: getUserPhoto,
                onRenderPersona: onRenderPersona,
                previousMessage: renderedMessages[index - 1] ?? null,
                toolbarMountNode: wrapperRef.current
            })))));
    };
    let content = null;
    if (renderedMessages.length === 0) {
        if (isLoading) {
            content = renderLoadingView();
        } else {
            content = renderEmptyView();
        }
    } else {
        content = renderMessagesView();
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.topButtonsSection
    }, Strings.TaskEditorDialog_TaskChat), content, /*#__PURE__*/ React.createElement("div", {
        className: classNames.chatComposeSection
    }, /*#__PURE__*/ React.createElement(ChatComposeSection, {
        recentMessages: renderedMessages.slice(-RecentMessagesLimit),
        prioritizedUserIdsForMentionSuggestions: prioritizedUserIdsForMentionSuggestions,
        focusOnInit: focusOnInit,
        callbacks: {
            onSendMessage: onSendMessageHandler,
            getMentionableUsers,
            onOpenAtMentionPicker: callbacks.onOpenAtMentionPicker,
            getUserPhoto,
            onRenderPersona
        }
    })));
};
